import React, {MouseEvent, TransitionEvent, type ReactElement} from "react";
import {css} from "/src/utils/utils";
import * as styles from "./expandable.module.scss";

export interface ExpandableClassesProps {
  collapseLabel?: string;
  expandLabel?: string;
  icon?: string;
  iconClosed?: string;
  iconOpened?: string;
  item?: string;
  itemClosed?: string;
  itemOpened?: string;
  title?: string;
  titleClosed?: string;
  titleOpened?: string;
}

export interface ExpandableProps {
  collapseLabel?: ReactElement | string;
  content: ReactElement | string;
  classes?: ExpandableClassesProps;
  expandLabel?: ReactElement | string;
  icon?: ReactElement;
  iconClosed?: ReactElement;
  iconOpened?: ReactElement;
  onClick?: (expanded: boolean) => void;
  open?: boolean;
  title: ReactElement | string;
  id: string;
}

/**
 * Design-agnostic component that mimics native <details> HTML element: hiding
 * and showing content when the trigger is clicked. Any styling should be come
 * from the component that encapsulates this.
 *
 * @param {ExpandableProps} props
 * @param {React.Element<any> | string} props.collapseLabel
 *        Additional trigger shown when `expanded` is true. (e.g. "Close")
 * @param {React.Element<any>} props.content Content that is shown when `expanded` is true.
 * @param {ExpandableClassesProps} props.classes Additional styles to be applied on various parts of the component.
 * @param {string} props.classes.collapseLabel Styles to be applied on `props.collapseLabel`.
 * @param {string} props.classes.expandLabel Styles to be applied on `props.expandLabel`.
 * @param {string} props.classes.icon Additional styles to be applied on the icon.
 * @param {string} props.classes.iconClosed Additional styles to be applied on the icon when `expanded` is false.
 * @param {string} props.classes.iconOpened Additional styles to be applied on the icon when `expanded` is true.
 * @param {string} props.classes.item Additional styles to be applied on the root component.
 * @param {string} props.classes.itemClosed
 *        Additional styles to be applied on the root component when `expanded` is false.
 * @param {string} props.classes.itemOpened
 *        Additional styles to be applied on the root component when `expanded` is true.
 * @param {string} props.classes.title Additional styles to be applied on the title.
 * @param {string} props.classes.titleClosed Additional styles to be applied on the title when `expanded` is false.
 * @param {string} props.classes.titleOpened Additional styles to be applied on the title when `expanded` is true.
 * @param {React.Element<any> | string} props.expandLabel
 *         Additional trigger shown when `expanded` is false. (e.g. "Read more")
 * @param {React.Element<any> | string} props.expandLabel
 *         Additional trigger shown when `expanded` is false. (e.g. "Read more")
 * @param {React.Element<any>} props.icon Icon that is rendered regardless of value of `expanded`.
 * @param {React.Element<any>} props.iconClosed Icon to render when `expanded` is false.
 * @param {React.Element<any>} props.iconOpened Icon to render when `expanded` is true.
 * @param {function} props.onClick Callback when the component is clicked on. Returns `expanded` value.
 * @param {boolean} props.open Whether the component's initial render should be in the open state.
 * @param {string} props.title Text displayed in the the title element.
 *
 * @return {ReactElement}
 */
const Expandable = (props: ExpandableProps): ReactElement => {
  /**
   * Extract props
   */
  const {
    collapseLabel,
    content,
    classes,
    expandLabel,
    icon,
    iconClosed,
    iconOpened,
    onClick,
    open,
    title,
    id,
  } = props;

  const $children = React.useRef<HTMLDivElement>(null);
  const $content = React.useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = React.useState(open || false);
  const contentID = `${id}-content`;

  /**
   * On mount callback.
   * Set initial state.
   */
  const onMount = (): void => {
    toggleContent(open || false);
  };

  /**
   * Handler for 'transitionend' event on content element.
   * @param {Event} e
   */
  const contentTransitionEnd = (e: TransitionEvent<HTMLDivElement>): void => {
    const $target: HTMLElement = e.target as HTMLElement;

    // don't do anything if it's not the content element.
    // we don't want any transitions within the children to continue with the
    // logic in this method.
    if ($content.current && $target.isSameNode($content.current) === false) {
      return;
    }

    // reset height to 'auto' and allow overflow so that we don't block any
    // visual changes within the children.
    if ($content.current && $content.current.offsetHeight > 0) {
      $content.current.style.height = "auto";
      $content.current.style.overflow = "visible";
    }
  };

  /**
   * Handler for 'click' event on title component.
   * @param {Event} e
   */
  const titleClick = (e: MouseEvent<HTMLButtonElement>): void => {
    e.preventDefault();
    toggleContent(!expanded);
  };

  /**
   * Hides or shows content.
   * @param {boolean} expand
   */
  const toggleContent = React.useCallback(
    (expand: boolean): void => {
      if (!$content.current || !$children.current || !$content.current.style) {
        return;
      }

      // inert the content, so that item in collapsed state
      // not focusable
      $content.current.inert = !expand;

      if (expand === true) {
        $content.current.style.height = `${$children.current.offsetHeight}px`;

        setExpanded(true);
      } else {
        // set the height again to override the 'auto'
        $content.current.style.height = `${$children.current.offsetHeight}px`;
        $content.current.style.overflow = "";

        requestAnimationFrame(() => {
          if ($content.current) {
            $content.current.style.height = "0px";
          }
        });

        setExpanded(false);
      }

      if (onClick) {
        onClick(expand);
      }
    },
    [onClick]
  );

  /**
   * Define on mount callback.
   * This cannot be declared on the top because using ES6 arrow functions
   * doesn't leverage on the hoisting that traditional functions benefit from.
   */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(onMount, []);

  React.useEffect(() => {
    toggleContent(open === true);
  }, [open, toggleContent]);

  /**
   * Figure out which icon we should be rendering.
   */
  const itemIcon = (() => {
    const iconClasses = css(
      styles.expandable__icon,
      classes?.icon!,
      expanded ? classes?.iconOpened! : classes?.iconClosed!
    );

    if (expanded && iconOpened) {
      return <div className={iconClasses}>{iconOpened}</div>;
    }

    if (!expanded && iconClosed) {
      return <div className={iconClasses}>{iconClosed}</div>;
    }

    if (icon) {
      return <div className={iconClasses}>{icon}</div>;
    }

    return null;
  })();

  /**
   * Render.
   */
  return (
    <div
      className={css(
        classes?.title!,
        classes?.item!,
        expanded ? classes?.itemOpened! : classes?.itemClosed!
      )}
    >
      <button
        className={css(
          styles.expandable__title,
          classes?.title!,
          expanded ? classes?.titleOpened! : classes?.titleClosed!
        )}
        tabIndex={0}
        onClick={titleClick}
        aria-expanded={expanded}
        aria-controls={contentID}
        id={id}
        type="button"
      >
        {title}
        {itemIcon}
      </button>

      <div
        className={css(styles.expandable__content)}
        ref={$content}
        onTransitionEnd={contentTransitionEnd}
        id={contentID}
        role="region"
        aria-labelledby={id}
        aria-hidden={!expanded}
      >
        <div ref={$children}>{content}</div>
      </div>

      {expandLabel || collapseLabel ? (
        <div className={css(styles.expandable__labels)}>
          {expandLabel && !expanded && (
            <button
              type="button"
              className={css(classes?.expandLabel!)}
              tabIndex={0}
              aria-expanded={expanded}
              aria-controls={contentID}
              onClick={() => toggleContent(true)}
            >
              {expandLabel}
            </button>
          )}
          {collapseLabel && expanded && (
            <button
              type="button"
              className={css(classes?.collapseLabel!)}
              tabIndex={0}
              aria-expanded={expanded}
              aria-controls={contentID}
              onClick={() => toggleContent(false)}
            >
              {collapseLabel}
            </button>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default Expandable;
